import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import './SuperAdminNodesViewer.css';

const SuperAdminNodesViewer = () => {
  const { t } = useTranslation();
  // États principaux
  const [nodes, setNodes] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeDetails, setNodeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  
  // Filtres
  const [filters, setFilters] = useState({
    tenantId: '',
    type: '',
    search: '',
    withTests: false
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 100,
    total: 0
  });

  // Ref pour le graphique simple
  const canvasRef = useRef();
  const [tenantColors, setTenantColors] = useState({});
  
  // États pour le canvas interactif
  const [canvasState, setCanvasState] = useState({
    zoomLevel: 1,
    panX: 0,
    panY: 0,
    layoutMode: 'circle' // 'circle', 'grid', 'force', 'hierarchy', 'cluster'
  });

  // Charger les données initiales
  useEffect(() => {
    loadData();
    loadTenants();
  }, [filters, pagination.offset]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // S'assurer que les paramètres numériques sont des entiers
      const cleanParams = {
        ...filters,
        offset: Math.floor(Number(pagination.offset)) || 0,
        limit: Math.floor(Number(pagination.limit)) || 100
      };
      
      const params = new URLSearchParams(cleanParams);
      
      console.log('🔍 Loading nodes with params:', cleanParams);
      console.log('🔍 Loading nodes with URL:', `/api/super-admin/nodes/all?${params}`);
      
      const response = await apiClient.get(`/api/super-admin/nodes/all?${params}`);
      console.log('✅ Nodes loaded:', response.data);
      
      // Debug spécifique des relations
      if (response.data.nodes) {
        const nodesWithRelations = response.data.nodes.filter(n => n.relationshipsCount > 0);
        console.log('🔗 Nœuds avec relations détectés:', nodesWithRelations);
        nodesWithRelations.forEach(node => {
          console.log(`   ${node.name}: ${node.relationshipsCount} relations, outgoingRelations:`, node.outgoingRelations);
        });
      }
      
      setNodes(response.data.nodes);
      setStats(response.data.stats);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
      
      // Générer des couleurs pour chaque tenant
      generateTenantColors(response.data.stats.tenantIds);
    } catch (error) {
      console.error('❌ Erreur chargement nodes:', error);
      console.error('Response:', error.response?.data);
      console.error('Status:', error.response?.status);
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      console.log('🔍 Loading tenants...');
      const response = await apiClient.get('/api/super-admin/tenants');
      console.log('✅ Tenants loaded:', response.data);
      setTenants(response.data.tenants);
    } catch (error) {
      console.error('❌ Erreur chargement tenants:', error);
      console.error('Response:', error.response?.data);
      console.error('Status:', error.response?.status);
    }
  };

  const generateTenantColors = (tenantIds) => {
    const colors = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7', '#ffecd2', '#fcb69f'];
    const colorMap = {};
    tenantIds.forEach((tenantId, index) => {
      colorMap[tenantId] = colors[index % colors.length];
    });
    setTenantColors(colorMap);
  };

  // Charger les détails d'un nœud
  const loadNodeDetails = async (nodeName, tenantId) => {
    try {
      const params = tenantId ? `?tenantId=${tenantId}` : '';
      const response = await apiClient.get(`/api/super-admin/nodes/${nodeName}${params}`);
      setNodeDetails(response.data);
    } catch (error) {
      console.error('Erreur chargement détails node:', error);
    }
  };

  // Gestion du zoom et du pan
  const handleZoomIn = () => {
    setCanvasState(prev => ({ 
      ...prev, 
      zoomLevel: Math.min(prev.zoomLevel * 1.2, 3) 
    }));
  };

  const handleZoomOut = () => {
    setCanvasState(prev => ({ 
      ...prev, 
      zoomLevel: Math.max(prev.zoomLevel / 1.2, 0.3) 
    }));
  };

  const handleResetZoom = () => {
    setCanvasState(prev => ({ 
      ...prev, 
      zoomLevel: 1, 
      panX: 0, 
      panY: 0 
    }));
  };

  const handleLayoutModeChange = (mode) => {
    setCanvasState(prev => ({ 
      ...prev, 
      layoutMode: mode,
      zoomLevel: 1,
      panX: 0,
      panY: 0
    }));
  };

  // Gestion de la sélection d'un nœud
  const handleNodeSelect = (node) => {
    setSelectedNode(node);
    loadNodeDetails(node.name, node.tenantId);
  };

  // Rendu du graphique simple
  useEffect(() => {
    if (!nodes.length || loading) return;
    
    renderSimpleGraph();
  }, [nodes, tenantColors, selectedNode, canvasState]);

  // Calculer les positions des nœuds selon le mode d'affichage
  const calculateNodePositions = () => {
    const canvas = canvasRef.current;
    if (!canvas) return {};
    
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    
    switch (canvasState.layoutMode) {
      case 'circle':
        return calculateCircleLayout(centerX, centerY, width, height);
      case 'grid':
        return calculateGridLayout(centerX, centerY, width, height);
      case 'force':
        return calculateForceLayout(centerX, centerY, width, height);
      case 'hierarchy':
        return calculateHierarchyLayout(centerX, centerY, width, height);
      case 'cluster':
        return calculateClusterLayout(centerX, centerY, width, height);
      default:
        return calculateCircleLayout(centerX, centerY, width, height);
    }
  };

  // Layout en cercle (existant)
  const calculateCircleLayout = (centerX, centerY, width, height) => {
    const positions = {};
    const maxRadius = Math.min(width, height) * 0.35;
    
    nodes.forEach((node, index) => {
      const angle = (index / nodes.length) * 2 * Math.PI;
      const radiusVariation = 0.7 + ((node.relationshipsCount || 0) / 10) * 0.3;
      const radius = maxRadius * radiusVariation;
      
      positions[node.name] = {
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius
      };
    });
    
    return positions;
  };

  // Layout en grille
  const calculateGridLayout = (centerX, centerY, width, height) => {
    const positions = {};
    const cols = Math.ceil(Math.sqrt(nodes.length));
    const rows = Math.ceil(nodes.length / cols);
    const cellWidth = Math.min(width * 0.8 / cols, 120);
    const cellHeight = Math.min(height * 0.8 / rows, 120);
    
    const startX = centerX - (cols * cellWidth) / 2;
    const startY = centerY - (rows * cellHeight) / 2;
    
    nodes.forEach((node, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      
      positions[node.name] = {
        x: startX + col * cellWidth + cellWidth / 2,
        y: startY + row * cellHeight + cellHeight / 2
      };
    });
    
    return positions;
  };

  // Layout force-directed (simplifié)
  const calculateForceLayout = (centerX, centerY, width, height) => {
    const positions = {};
    
    // Initialisation aléatoire proche du centre
    nodes.forEach((node, index) => {
      const angle = Math.random() * 2 * Math.PI;
      const radius = Math.random() * 100;
      
      positions[node.name] = {
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius
      };
    });
    
    // Simulation force simplifiée (quelques itérations)
    for (let iteration = 0; iteration < 50; iteration++) {
      nodes.forEach((node) => {
        const pos = positions[node.name];
        let fx = 0, fy = 0;
        
        // Force de répulsion entre nœuds
        nodes.forEach((other) => {
          if (other.name === node.name) return;
          const otherPos = positions[other.name];
          const dx = pos.x - otherPos.x;
          const dy = pos.y - otherPos.y;
          const distance = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = 1000 / (distance * distance);
          
          fx += (dx / distance) * force;
          fy += (dy / distance) * force;
        });
        
        // Force d'attraction vers le centre
        const centerDx = centerX - pos.x;
        const centerDy = centerY - pos.y;
        fx += centerDx * 0.01;
        fy += centerDy * 0.01;
        
        // Force d'attraction des relations
        if (node.outgoingRelations) {
          node.outgoingRelations.forEach(relation => {
            const target = nodes.find(n => n.name === relation.target);
            if (target) {
              const targetPos = positions[target.name];
              const dx = targetPos.x - pos.x;
              const dy = targetPos.y - pos.y;
              const distance = Math.sqrt(dx * dx + dy * dy) || 1;
              const force = distance * 0.02;
              
              fx += (dx / distance) * force;
              fy += (dy / distance) * force;
            }
          });
        }
        
        // Appliquer les forces avec amortissement
        pos.x += fx * 0.1;
        pos.y += fy * 0.1;
        
        // Contraintes de bornes
        pos.x = Math.max(50, Math.min(width - 50, pos.x));
        pos.y = Math.max(50, Math.min(height - 50, pos.y));
      });
    }
    
    return positions;
  };

  // Layout hiérarchique (basé sur les relations)
  const calculateHierarchyLayout = (centerX, centerY, width, height) => {
    const positions = {};
    
    // Calculer les niveaux hiérarchiques
    const levels = {};
    const visited = new Set();
    
    // Trouver les nœuds racines (pas de relations entrantes)
    const rootNodes = nodes.filter(node => {
      return !nodes.some(other => 
        other.outgoingRelations?.some(rel => rel.target === node.name)
      );
    });
    
    // BFS pour assigner les niveaux
    const queue = rootNodes.map(node => ({ node, level: 0 }));
    
    while (queue.length > 0) {
      const { node, level } = queue.shift();
      
      if (visited.has(node.name)) continue;
      visited.add(node.name);
      
      if (!levels[level]) levels[level] = [];
      levels[level].push(node);
      
      // Ajouter les enfants au niveau suivant
      if (node.outgoingRelations) {
        node.outgoingRelations.forEach(relation => {
          const target = nodes.find(n => n.name === relation.target);
          if (target && !visited.has(target.name)) {
            queue.push({ node: target, level: level + 1 });
          }
        });
      }
    }
    
    // Positionner les nœuds non visités (cycles ou isolés)
    nodes.forEach(node => {
      if (!visited.has(node.name)) {
        const maxLevel = Math.max(...Object.keys(levels).map(Number));
        const level = maxLevel + 1;
        if (!levels[level]) levels[level] = [];
        levels[level].push(node);
      }
    });
    
    // Calculer les positions
    const levelCount = Object.keys(levels).length;
    const levelHeight = height * 0.8 / (levelCount || 1);
    const startY = centerY - (levelHeight * levelCount) / 2;
    
    Object.entries(levels).forEach(([level, levelNodes]) => {
      const levelIndex = parseInt(level);
      const nodeWidth = width * 0.8 / (levelNodes.length || 1);
      const startX = centerX - (nodeWidth * levelNodes.length) / 2;
      
      levelNodes.forEach((node, index) => {
        positions[node.name] = {
          x: startX + index * nodeWidth + nodeWidth / 2,
          y: startY + levelIndex * levelHeight + levelHeight / 2
        };
      });
    });
    
    return positions;
  };

  // Layout en clusters (par tenant)
  const calculateClusterLayout = (centerX, centerY, width, height) => {
    const positions = {};
    const tenantGroups = {};
    
    // Grouper par tenant
    nodes.forEach(node => {
      if (!tenantGroups[node.tenantId]) {
        tenantGroups[node.tenantId] = [];
      }
      tenantGroups[node.tenantId].push(node);
    });
    
    const tenantIds = Object.keys(tenantGroups);
    const clusterCount = tenantIds.length;
    
    tenantIds.forEach((tenantId, tenantIndex) => {
      const tenantNodes = tenantGroups[tenantId];
      
      // Position du cluster
      const clusterAngle = (tenantIndex / clusterCount) * 2 * Math.PI;
      const clusterRadius = Math.min(width, height) * 0.25;
      const clusterCenterX = centerX + Math.cos(clusterAngle) * clusterRadius;
      const clusterCenterY = centerY + Math.sin(clusterAngle) * clusterRadius;
      
      // Disposer les nœuds dans le cluster
      const subRadius = Math.min(80, Math.max(30, tenantNodes.length * 8));
      
      tenantNodes.forEach((node, nodeIndex) => {
        const nodeAngle = (nodeIndex / tenantNodes.length) * 2 * Math.PI;
        const nodeRadius = subRadius * (0.5 + ((node.relationshipsCount || 0) / 20) * 0.5);
        
        positions[node.name] = {
          x: clusterCenterX + Math.cos(nodeAngle) * nodeRadius,
          y: clusterCenterY + Math.sin(nodeAngle) * nodeRadius
        };
      });
    });
    
    return positions;
  };

  const renderSimpleGraph = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas avec arrière-plan
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, width, height);
    
    // Appliquer les transformations de zoom et pan
    ctx.save();
    ctx.translate(width / 2 + canvasState.panX, height / 2 + canvasState.panY);
    ctx.scale(canvasState.zoomLevel, canvasState.zoomLevel);
    ctx.translate(-width / 2, -height / 2);
    
    // Grille de fond subtile (adaptée au zoom)
    ctx.strokeStyle = 'rgba(102, 126, 234, 0.1)';
    ctx.lineWidth = 1 / canvasState.zoomLevel;
    const gridSize = 40;
    for (let x = -width; x <= width * 2; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, -height);
      ctx.lineTo(x, height * 2);
      ctx.stroke();
    }
    for (let y = -height; y <= height * 2; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(-width, y);
      ctx.lineTo(width * 2, y);
      ctx.stroke();
    }
    
    if (nodes.length === 0) {
      ctx.restore();
      return;
    }
    
    // Calculer les positions selon le mode d'affichage
    const positions = calculateNodePositions();
    
    // Dessiner les connexions si un nœud est sélectionné
    if (selectedNode && selectedNode.outgoingRelations) {
      selectedNode.outgoingRelations.forEach(relation => {
        const targetNode = nodes.find(n => n.name === relation.target);
        if (targetNode) {
          const sourcePos = positions[selectedNode.name];
          const targetPos = positions[targetNode.name];
          if (sourcePos && targetPos) {
            // Ligne de connexion
            ctx.beginPath();
            ctx.moveTo(sourcePos.x, sourcePos.y);
            ctx.lineTo(targetPos.x, targetPos.y);
            ctx.strokeStyle = 'rgba(245, 87, 108, 0.6)';
            ctx.lineWidth = 2 / canvasState.zoomLevel;
            ctx.stroke();
            
            // Flèche
            drawArrow(ctx, sourcePos.x, sourcePos.y, targetPos.x, targetPos.y, 'rgba(245, 87, 108, 0.8)');
          }
        }
      });
    }
    
    // Dessiner les nœuds
    nodes.forEach((node) => {
      const pos = positions[node.name];
      if (!pos) return;
      
      const { x, y } = pos;
      
      // Taille du nœud basée sur les relations
      const nodeRadius = Math.max(12, Math.min(25, 12 + (node.relationshipsCount || 0) * 2));
      
      // Couleur du tenant avec dégradé
      const color = tenantColors[node.tenantId] || '#999';
      
      // Ombre portée
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 4 / canvasState.zoomLevel;
      ctx.shadowOffsetX = 2 / canvasState.zoomLevel;
      ctx.shadowOffsetY = 2 / canvasState.zoomLevel;
      
      // Cercle principal avec dégradé
      const gradient = ctx.createRadialGradient(x - 5, y - 5, 0, x, y, nodeRadius);
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, darkenColor(color, 20));
      
      ctx.beginPath();
      ctx.arc(x, y, nodeRadius, 0, 2 * Math.PI);
      ctx.fillStyle = gradient;
      ctx.fill();
      
      // Réinitialiser l'ombre
      ctx.shadowColor = 'transparent';
      
      // Bordure selon l'état
      ctx.beginPath();
      ctx.arc(x, y, nodeRadius, 0, 2 * Math.PI);
      if (selectedNode?.name === node.name) {
        ctx.strokeStyle = '#ff6b35';
        ctx.lineWidth = 4 / canvasState.zoomLevel;
        ctx.stroke();
        
        // Effet de pulsation
        ctx.beginPath();
        ctx.arc(x, y, nodeRadius + 8, 0, 2 * Math.PI);
        ctx.strokeStyle = 'rgba(255, 107, 53, 0.3)';
        ctx.lineWidth = 2 / canvasState.zoomLevel;
        ctx.stroke();
      } else {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2 / canvasState.zoomLevel;
        ctx.stroke();
      }
      
      // Indicateurs visuels
      const iconSize = Math.max(8, nodeRadius * 0.4);
      ctx.font = `${iconSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Icône test en haut
      if (node.hasTests) {
        ctx.fillStyle = '#43e97b';
        ctx.fillText('🧪', x, y - nodeRadius - 15);
      }
      
      // Nombre de relations en bas
      if ((node.relationshipsCount || 0) > 0) {
        ctx.fillStyle = '#f5576c';
        ctx.font = `bold ${Math.max(10, iconSize)}px Arial`;
        ctx.fillText(node.relationshipsCount, x, y + nodeRadius + 15);
      }
      
      // Nom du nœud
      ctx.fillStyle = '#2d3748';
      ctx.font = `${Math.max(9, iconSize - 2)}px Arial`;
      const displayName = node.name.length > 15 ? node.name.substring(0, 15) + '...' : node.name;
      
      // Arrière-plan pour le texte
      const textWidth = ctx.measureText(displayName).width;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(x - textWidth/2 - 4, y + nodeRadius + 25 - 8, textWidth + 8, 16);
      
      ctx.fillStyle = '#2d3748';
      ctx.fillText(displayName, x, y + nodeRadius + 25);
      
      // Stocker la position pour la détection de clic (ajustée pour le zoom)
      node._canvasX = x;
      node._canvasY = y;
      node._canvasRadius = nodeRadius;
    });
    
    ctx.restore();
  };

  // Fonction utilitaire pour assombrir une couleur
  const darkenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  };

  // Fonction pour dessiner une flèche
  const drawArrow = (ctx, fromX, fromY, toX, toY, color) => {
    const headlen = 10;
    const angle = Math.atan2(toY - fromY, toX - fromX);
    
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 2 / canvasState.zoomLevel;
    
    // Pointe de la flèche
    ctx.beginPath();
    ctx.moveTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(toX, toY);
    ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
    ctx.fill();
  };

  // Gestionnaire de clics amélioré avec support du zoom
  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    // Ajuster les coordonnées pour le zoom et le pan
    const adjustedX = (clickX - canvas.width / 2 - canvasState.panX) / canvasState.zoomLevel + canvas.width / 2;
    const adjustedY = (clickY - canvas.height / 2 - canvasState.panY) / canvasState.zoomLevel + canvas.height / 2;

    // Vérifier si on clique sur un nœud
    for (let node of nodes) {
      if (node._canvasX && node._canvasY && node._canvasRadius) {
        const distance = Math.sqrt(
          (adjustedX - node._canvasX) ** 2 + (adjustedY - node._canvasY) ** 2
        );
        if (distance <= node._canvasRadius + 5) { // Petite marge
          handleNodeSelect(node);
          return;
        }
      }
    }
    
    // Si on clique ailleurs, déselectionner
    setSelectedNode(null);
    setNodeDetails(null);
  };

  // Ajouter le gestionnaire de clics
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('click', handleCanvasClick);
      return () => canvas.removeEventListener('click', handleCanvasClick);
    }
  }, [nodes, canvasState]);

  // Gestionnaires de filtres
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const clearFilters = () => {
    setFilters({
      tenantId: '',
      type: '',
      search: '',
      withTests: false
    });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  return (
    <div className="super-admin-nodes-viewer">
      {/* Header avec stats */}
      <div className="viewer-header">
        <div className="header-title">
          <h1>🌐 Vue Globale des Nœuds</h1>
          <p>Explorez et analysez l'ensemble du réseau de nœuds multi-tenant</p>
        </div>

        <div className="header-stats">
          <div className="stat-card total-nodes">
            <span className="stat-icon">📊</span>
            <div className="stat-content">
              <h3>{stats.totalNodes || 0}</h3>
              <p>Nœuds totaux</p>
            </div>
          </div>
          <div className="stat-card total-tenants">
            <span className="stat-icon">🏢</span>
            <div className="stat-content">
              <h3>{stats.totalTenants || 0}</h3>
              <p>Organisations</p>
            </div>
          </div>
          <div className="stat-card total-types">
            <span className="stat-icon">🏷️</span>
            <div className="stat-content">
              <h3>{stats.totalTypes || 0}</h3>
              <p>Types de nœuds</p>
            </div>
          </div>
          <div className="stat-card with-tests">
            <span className="stat-icon">🧪</span>
            <div className="stat-content">
              <h3>{stats.withTests || 0}</h3>
              <p>Avec tests</p>
            </div>
          </div>
          <div className="stat-card with-relations">
            <span className="stat-icon">🔗</span>
            <div className="stat-content">
              <h3>{stats.withRelations || 0}</h3>
              <p>Avec relations</p>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="filters-section">
          <div className="filters-header">
            <h3>🔍 Filtres & Recherche</h3>
            <button onClick={clearFilters} className="clear-filters-btn">
              🗑️ Réinitialiser
            </button>
          </div>
          
          <div className="filters-grid">
            <div className="filter-group">
              <label>🏢 Organisation</label>
              <select 
                value={filters.tenantId}
                onChange={(e) => handleFilterChange('tenantId', e.target.value)}
                className="filter-select"
              >
                <option value="">Toutes les organisations</option>
                {tenants.map(tenant => (
                  <option key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>🔖 Type de Nœud</label>
              <select 
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="filter-select"
              >
                <option value="">Tous les types</option>
                {stats.nodeTypes?.map(type => (
                  <option key={type} value={type}>{type || 'Non défini'}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>🔍 Recherche</label>
              <input
                type="text"
                placeholder={t('placeholders.nameOrDescription')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.withTests}
                  onChange={(e) => handleFilterChange('withTests', e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                🧪 Uniquement avec tests
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="viewer-content">
        {/* Section graphique étendue */}
        <div className={`graph-section ${selectedNode ? 'with-details' : 'full-width'}`}>
          <div className="graph-header">
            <h3>🎯 Réseau de Nœuds Interactif</h3>
            <p>Cliquez sur un nœud pour explorer ses connexions • Cliquez ailleurs pour désélectionner</p>
            <div className="graph-controls">
              <div className="layout-controls">
                <span className="layout-label">Mode d'affichage:</span>
                <div className="layout-buttons">
                  <button 
                    className={`layout-btn ${canvasState.layoutMode === 'circle' ? 'active' : ''}`}
                    onClick={() => handleLayoutModeChange('circle')}
                    title={t('titles.circleLayout')}
                  >
                    ⭕ Cercle
                  </button>
                  <button 
                    className={`layout-btn ${canvasState.layoutMode === 'grid' ? 'active' : ''}`}
                    onClick={() => handleLayoutModeChange('grid')}
                    title={t('titles.gridLayout')}
                  >
                    ⚏ Grille
                  </button>
                  <button 
                    className={`layout-btn ${canvasState.layoutMode === 'force' ? 'active' : ''}`}
                    onClick={() => handleLayoutModeChange('force')}
                    title={t('titles.forceDirected')}
                  >
                    🌊 Force
                  </button>
                  <button 
                    className={`layout-btn ${canvasState.layoutMode === 'hierarchy' ? 'active' : ''}`}
                    onClick={() => handleLayoutModeChange('hierarchy')}
                    title={t('titles.hierarchicalLayout')}
                  >
                    🌳 Hiérarchie
                  </button>
                  <button 
                    className={`layout-btn ${canvasState.layoutMode === 'cluster' ? 'active' : ''}`}
                    onClick={() => handleLayoutModeChange('cluster')}
                    title={t('titles.organizationClusters')}
                  >
                    🗂️ Clusters
                  </button>
                </div>
              </div>
              
              <div className="canvas-actions">
                <button 
                  className="reset-selection-btn"
                  onClick={() => {
                    setSelectedNode(null);
                    setNodeDetails(null);
                  }}
                  disabled={!selectedNode}
                >
                  🔄 Réinitialiser la sélection
                </button>
                <div className="zoom-controls">
                  <span className="zoom-label">Zoom: {Math.round(canvasState.zoomLevel * 100)}%</span>
                  <button className="zoom-btn" onClick={handleZoomIn}>🔍+</button>
                  <button className="zoom-btn" onClick={handleZoomOut}>🔍-</button>
                  <button className="zoom-btn" onClick={handleResetZoom} title={t('titles.resetZoom')}>🎯</button>
                </div>
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="loading-graph">
              <div className="spinner"></div>
              <p>Génération du réseau de nœuds...</p>
            </div>
          ) : (
            <div className="graph-container">
              <div className="canvas-wrapper">
                <canvas
                  ref={canvasRef}
                  width="800"
                  height="600"
                  className="nodes-canvas"
                  style={{ cursor: selectedNode ? 'pointer' : 'grab' }}
                />
                {selectedNode && (
                  <div className="canvas-overlay">
                    <div className="selected-node-info">
                      <h4>{selectedNode.name}</h4>
                      <p>{selectedNode.type || 'Type non défini'}</p>
                      <div className="quick-stats">
                        <span className="stat">🔗 {selectedNode.relationshipsCount || 0}</span>
                        <span className="stat">{selectedNode.hasTests ? '🧪 Tests' : '❌ Pas de tests'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Légende interactive */}
              <div className="graph-legend">
                <h4>🏢 Organisations</h4>
                <div className="legend-list">
                  {Object.entries(tenantColors).map(([tenantId, color]) => {
                    const tenant = tenants.find(t => t.tenantId === tenantId);
                    const nodeCount = nodes.filter(n => n.tenantId === tenantId).length;
                    return (
                      <div key={tenantId} className="legend-item">
                        <div 
                          className="legend-color-indicator" 
                          style={{ backgroundColor: color }}
                        ></div>
                        <div className="legend-content">
                          <span className="legend-name">{tenant ? tenant.name : tenantId}</span>
                          <span className="legend-count">({nodeCount} nœuds)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="legend-symbols">
                  <h5>Symboles</h5>
                  <div className="symbol-item">
                    <span className="symbol">🧪</span>
                    <span className="symbol-desc">Contient des tests</span>
                  </div>
                  <div className="symbol-item">
                    <span className="symbol">🔗</span>
                    <span className="symbol-desc">Nombre de relations</span>
                  </div>
                  <div className="symbol-item">
                    <span className="symbol">📏</span>
                    <span className="symbol-desc">Taille = Nb relations</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Panneau détails conditionnel */}
        {selectedNode && (
          <div className="node-details-panel">
            <div className="details-header">
              <div className="details-title">
                <h3>📋 Détails du Nœud</h3>
                <div className="node-title-info">
                  <span className="node-name">{selectedNode.name}</span>
                  <span 
                    className="node-org-indicator"
                    style={{ backgroundColor: tenantColors[selectedNode.tenantId] }}
                  >
                    {tenants.find(t => t.tenantId === selectedNode.tenantId)?.name || selectedNode.tenantId}
                  </span>
                </div>
              </div>
              <button 
                onClick={() => {
                  setSelectedNode(null);
                  setNodeDetails(null);
                }}
                className="close-details-btn"
                title={t('titles.closeDetails')}
              >
                ✕
              </button>
            </div>

            <div className="details-content">
              <div className="detail-section">
                <h4>ℹ️ Informations Générales</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>📛 Nom:</label>
                    <span className="detail-value">{selectedNode.name}</span>
                  </div>
                  <div className="detail-item">
                    <label>🏷️ Type:</label>
                    <span className="detail-value type-badge">{selectedNode.type || 'Non défini'}</span>
                  </div>
                  <div className="detail-item">
                    <label>🏢 Organisation:</label>
                    <span className="detail-value org-badge" style={{ backgroundColor: `${tenantColors[selectedNode.tenantId]}20`, color: tenantColors[selectedNode.tenantId] }}>
                      {tenants.find(t => t.tenantId === selectedNode.tenantId)?.name || selectedNode.tenantId}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>🔗 Relations:</label>
                    <span className="detail-value relations-count">{selectedNode.relationshipsCount || 0}</span>
                  </div>
                  <div className="detail-item">
                    <label>🧪 Tests:</label>
                    <span className={`detail-value tests-status ${selectedNode.hasTests ? 'has-tests' : 'no-tests'}`}>
                      {selectedNode.hasTests ? '✅ Disponibles' : '❌ Aucun'}
                    </span>
                  </div>
                </div>
              </div>

              {selectedNode.description && (
                <div className="detail-section">
                  <h4>📝 Description</h4>
                  <div className="description-content">
                    <p>{selectedNode.description}</p>
                  </div>
                </div>
              )}

              {selectedNode.outgoingRelations && selectedNode.outgoingRelations.length > 0 && (
                <div className="detail-section">
                  <h4>🔗 Relations Sortantes ({selectedNode.outgoingRelations.length})</h4>
                  <div className="relations-grid">
                    {selectedNode.outgoingRelations.map((rel, idx) => (
                      <div key={idx} className="relation-card">
                        <div className="relation-type">{rel.type || 'RELATION'}</div>
                        <div className="relation-target">
                          <span className="target-arrow">→</span>
                          <span className="target-name">{rel.target}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {nodeDetails && (
                <>
                  {nodeDetails.relationships.incoming.length > 0 && (
                    <div className="detail-section">
                      <h4>🔙 Relations Entrantes ({nodeDetails.relationships.incoming.length})</h4>
                      <div className="relations-grid">
                        {nodeDetails.relationships.incoming.map((rel, idx) => (
                          <div key={idx} className="relation-card incoming">
                            <div className="relation-type">{rel.type || 'RELATION'}</div>
                            <div className="relation-source">
                              <span className="source-name">{rel.source}</span>
                              <span className="source-arrow">→</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {nodeDetails.node.tests && nodeDetails.stats.testsCount > 0 && (
                    <div className="detail-section">
                      <h4>🧪 Tests ({nodeDetails.stats.testsCount})</h4>
                      <div className="tests-grid">
                        {nodeDetails.node.tests.scenarios?.slice(0, 5).map((scenario, idx) => (
                          <div key={idx} className="test-card">
                            <div className="test-title">{scenario.title || `Scénario ${idx + 1}`}</div>
                            <div className="test-type">{scenario.type || 'Non spécifié'}</div>
                          </div>
                        ))}
                        {nodeDetails.node.tests.scenarios?.length > 5 && (
                          <div className="test-card more-tests">
                            <div className="more-count">+{nodeDetails.node.tests.scenarios.length - 5}</div>
                            <div className="more-text">autres scénarios</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminNodesViewer;
